/**
 * Gallery and tier selector.
 *
 * Each slot in a multi-item tier holds its own colour and size, so a 3-pack can
 * be three different colourways in three different sizes. That is why slot state
 * is an array of independent objects rather than one selected variant.
 *
 * The colour and size controls exist ONCE and write into whichever slot is
 * active. Repeating both rows per item tripled the height of the buy box and
 * made three identical grids compete for attention; a strip of slot chips
 * carries the same information in one line.
 *
 * Money is never written into this file. Both the price and the struck
 * compare-at figure come from the variant data, and whether they are shown
 * with pence follows the store's own currency setting rather than a rule
 * decided here.
 */
(function () {
  'use strict';

  if (customElements.get('product-buy')) return;

  /* A colourway paints itself from design tokens named after it: Eclipse reads
     --sw-eclipse, and --sw-eclipse-2 if it carries a second tone. The second
     falls back to the first, so a plain colourway needs no second token and no
     separate class: its diagonal split is simply invisible. Adding a colourway
     is therefore a token and nothing else, where it used to mean editing a map
     in here as well, which is how a colourway ends up with no swatch. */
  /* The picture that sits inside a swatch. Decorative: the button already
     carries the colour name as its label, so announcing the image again would
     read the colourway twice to a screen reader. */
  const swatchImage = (src, name) => {
    const img = document.createElement('img');
    img.src = src;
    img.alt = '';
    img.loading = 'lazy';
    img.decoding = 'async';
    img.width = 60;
    img.height = 60;
    return img;
  };

  const swatchVars = (name) => {
    const slug = String(name).toLowerCase().replace(/\s*\/\s*/g, '-').replace(/\s+/g, '-');
    return { base: `var(--sw-${slug})`, accent: `var(--sw-${slug}-2, var(--sw-${slug}))` };
  };

  class ProductBuy extends HTMLElement {
    connectedCallback() {
      const raw = this.querySelector('[data-pb-data]');
      if (!raw) return;
      this.data = JSON.parse(raw.textContent);

      this.paidQty = parseInt(this.dataset.paidQty, 10) || 2;
      this.freeQty = parseInt(this.dataset.freeQty, 10) || 1;
      this.comparePct = parseInt(this.dataset.comparePct, 10) || 0;
      this.saveLabel = this.dataset.saveLabel || 'You save';

      this.stage = this.querySelector('[data-pb-stage]');
      this.track = this.querySelector('[data-pb-track]');
      this.dots = Array.from(this.querySelectorAll('[data-pb-dot]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-pb-thumb]'));
      this.thumbsHost = this.querySelector('[data-pb-thumbs]');
      this.picker = this.querySelector('[data-pb-picker]');
      this.pickerHead = this.querySelector('[data-pb-picker-head]');
      this.pickerTitleEl = this.querySelector('[data-pb-picker-title]');
      this.progressEl = this.querySelector('[data-pb-progress]');
      this.slotRow = this.querySelector('[data-pb-slotrow]');
      this.swatchHost = this.querySelector('[data-pb-swatches]');
      this.sizeHost = this.querySelector('[data-pb-sizes]');
      this.colourNameEl = this.querySelector('[data-pb-colour-name]');
      this.atc = this.querySelector('[data-pb-atc]');
      this.atcLabelEl = this.querySelector('[data-pb-atc-label]');
      this.atcLabel = this.atcLabelEl ? this.atcLabelEl.textContent.trim() : 'Add to cart';
      this.note = this.querySelector('[data-pb-atc-note]');
      this.tierEls = Array.from(this.querySelectorAll('[data-pb-tier]'));
      this.initSocial();

      this.sgDialog = this.querySelector('[data-pb-sizeguide-dialog]');
      this.giftEls = Array.from(this.querySelectorAll('[data-pb-gift]'));
      this.giftTitle = this.querySelector('[data-pb-gifts-title]');
      this.giftCount = this.querySelector('[data-pb-gifts-count]');
      this.giftsLockedTitle = this.giftTitle ? this.giftTitle.textContent : '';

      this.unit = this.data.variants.length ? this.data.variants[0].price : 0;
      this.unitCompare = this.data.variants.length ? (this.data.variants[0].compareAt || 0) : 0;
      const currency = this.data.currency || 'GBP';
      this.money = new Intl.NumberFormat('en-GB', { style: 'currency', currency });
      // Whole amounts lose the trailing pence, matching Liquid's
      // money_without_trailing_zeros, so the figures this file renders and the
      // ones rendered server-side beside them cannot disagree. Anything with
      // pence in it keeps them, so a part-pound total can never be rounded on
      // screen into something the cart will not charge.
      this.moneyWhole = new Intl.NumberFormat('en-GB', {
        style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0
      });

      this.chipEls = [];
      this.lastSize = null;

      this.tierEls.forEach((el) =>
        el.addEventListener('click', () => this.setTier(parseInt(el.dataset.pbTier, 10), el))
      );
      this.atc.addEventListener('click', () => this.addToCart());
      this.track.addEventListener('swipe:change', (e) => this.syncDots(e.detail.index));
      this.dots.forEach((d, i) => d.addEventListener('click', () => this.goTo(i)));
      this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.goTo(i)));
      const prev = this.querySelector('[data-pb-prev]');
      const next = this.querySelector('[data-pb-next]');
      this.prevBtn = prev;
      this.nextBtn = next;
      if (prev) prev.addEventListener('click', () => this.step(-1));
      if (next) next.addEventListener('click', () => this.step(1));
      if (this.thumbsHost) {
        this.thumbsHost.addEventListener('scroll', () => this.markStrip(), { passive: true });
        window.addEventListener('resize', () => this.markStrip());
      }

      this.buildOptionRows();
      // The strip only ever caught up on a scroll, so the page opened with no
      // frame marked and the first tap had to correct the ring rather than
      // move it.
      this.syncDots();
      this.wireStrips();
      this.wireSizeGuide();
      this.renderTierPrices();

      // The tier marked in the section is the one the page opens on; the first
      // card is only a fallback for a section where none is marked.
      const pre = this.tierEls.find((el) => el.hasAttribute('data-pb-preselect')) || this.tierEls[0];
      this.setTier(pre ? parseInt(pre.dataset.pbTier, 10) : 1, pre);
    }

    /* ---------- pricing ---------- */


    // How many of the items in a tier are actually paid for. One in every
    // three is free, which is the same ratio the discount applies, so the
    // figure on the card and the figure the cart charges come from one rule.
    // How many garments in a tier are actually paid for. One in every three is
    // free, and the free one is not a cart line at all: a theme cannot make a
    // line cost nothing, so it travels as a line item property and the two
    // paid lines come to exactly the figure on the card.
    initSocial() {
      for (const img of this.querySelectorAll('[data-pb-avatar]')) {
        const miss = () => img.classList.add('is-missing');
        if (img.complete && img.naturalWidth === 0 && img.currentSrc) miss();
        else img.addEventListener('error', miss, { once: true });
      }
      for (const host of this.querySelectorAll('[data-pb-social]')) {
        const row = host.querySelector('[data-pb-social-row]');
        const thumb = host.querySelector('[data-pb-social-thumb]');
        if (!row || !thumb) continue;
        let queued = false;
        const paint = () => {
          queued = false;
          if (!row.clientWidth) return;
          const span = row.scrollWidth - row.clientWidth;
          const share = row.clientWidth / row.scrollWidth;
          const at = span > 0 ? row.scrollLeft / span : 0;
          thumb.style.width = `${share * 100}%`;
          thumb.style.transform = `translate3d(${at * (1 / share - 1) * 100}%, 0, 0)`;
        };
        const ask = () => {
          if (queued) return;
          queued = true;
          requestAnimationFrame(paint);
        };
        row.addEventListener('scroll', ask, { passive: true });
        window.addEventListener('resize', ask, { passive: true });
        paint();
      }
    }

    // Shopify runs two offers per product, buy 2 get 1 and buy 3 get 2, and
    // gives each cart whichever one frees more. This mirrors that choice, and
    // theme.js holds the same rule, so the card, the cart and checkout agree.
    freeFor(paid) {
      return Math.max(Math.floor(paid / 2), 2 * Math.floor(paid / 3));
    }

    paidUnits(qty) {
      let paid = 1;
      while (paid + this.freeFor(paid) < qty) paid += 1;
      return Math.min(paid, qty);
    }

    // What the tier costs, and it must be what the cart will charge for it.
    //
    // There used to be a second arm here that priced a multi-item tier as
    // "pay for paidQty, get freeQty free" whenever the offer product did not
    // resolve. It is gone, and it must not come back. A free item only exists
    // because the offer product exists: with no product to add, addToCart puts
    // one full-price line in the cart per item, so the derived figure was
    // advertising a total the cart would not charge. The offer product is
    // missing on this store today, and that arm silently showed a three-pack
    // at two items' money.
    //
    // If the free item is wanted without an offer product, the answer is not
    // arithmetic in here: it is a real product at a real price. An automatic
    // discount is not the answer either, per the note in HANDOVER.md. That
    // product exists again, so this reads its price and nothing is derived.
    totalFor(qty) {
      return this.paidUnits(qty) * this.unit;
    }

    // Struck compare-at figure, read from Shopify rather than worked out here.
    //
    // Each tier strikes its own product's compare-at price: the offer tier the
    // offer product's, every other tier the garment's own, multiplied by how
    // many are in the tier. That figure is set on the variant, so it converts
    // with the market like every other price, it can end in .99, and the theme
    // holds no copy of it to go stale.
    //
    // The section's percentage is only a fallback for a product with no
    // compare-at set. It rounds UP to the nearest amount ending in .99 so the
    // price ending holds in every currency.
    compareFor(total, qty) {
      if (!(total > 0)) return null;

      const set = this.unitCompare * qty;
      if (set > total) return set;

      /* A product with no compare-at still has an honest "before" on the offer
         tier: what those same garments cost bought one at a time. Nothing is
         invented here, and on the single tier it equals the total, so it falls
         through and no saving is claimed. */
      const straight = this.unit * qty;
      if (straight > total) return straight;

      if (!(this.comparePct > 0)) return null;
      const raw = Math.round(total * (100 + this.comparePct) / 100);
      let cmp = Math.floor(raw / 100) * 100 + 99;
      if (cmp < raw) cmp += 100;
      return cmp > total ? cmp : null;
    }

    fmt(pence) {
      return (pence % 100 === 0 ? this.moneyWhole : this.money).format(pence / 100);
    }

    // What the tier saves, stated in money. It is always the struck figure on
    // the same card minus the price on the same card, so the three numbers a
    // customer can see subtract to each other.
    savingFor(qty, total) {
      // Measured against the struck figure on the same card whenever there is
      // one, never against a second reference. Two numbers on one card that
      // do not subtract to the third is the fastest way to lose someone who
      // checks the arithmetic.
      const cmp = this.compareFor(total, qty);
      if (cmp) return cmp - total;
      const straight = this.unit * qty;
      return straight > total ? straight - total : 0;
    }

    renderTierPrices() {
      this.tierEls.forEach((el) => {
        const qty = parseInt(el.dataset.pbTier, 10);
        const total = this.totalFor(qty);
        const p = el.querySelector('[data-pb-tier-price]');
        if (p) p.textContent = this.fmt(total);

        const c = el.querySelector('[data-pb-tier-compare]');
        if (c) {
          const cmp = this.compareFor(total, qty);
          c.textContent = cmp ? this.fmt(cmp) : '';
          c.hidden = !cmp;
        }

        const sub = el.querySelector('[data-pb-tier-sub]');
        if (sub) {
          const save = this.savingFor(qty, total);
          sub.classList.toggle('is-save', save > 0);
          sub.textContent = save > 0
            ? `${this.saveLabel} ${this.fmt(save)}`
            : (el.dataset.sub || '');
        }
      });
    }

    /* ---------- tiers and slots ---------- */

    setTier(qty, el) {
      this.qty = qty;
      this.tierEls.forEach((t) => {
        const on = t === el;
        t.setAttribute('aria-checked', on ? 'true' : 'false');
        t.classList.toggle('is-on', on);
        const card = t.closest('[data-pb-tier-card]');
        if (card) card.classList.toggle('is-on', on);
      });

      // The picker travels to whichever tier is chosen rather than being
      // duplicated per card. One element means one set of listeners, one piece
      // of state, and no way for two copies to disagree about what is picked.
      if (el && this.picker) {
        const host = (el.closest('[data-pb-tier-card]') || el).querySelector('[data-pb-tier-slot]');
        if (host && this.picker.parentElement !== host) host.appendChild(this.picker);
      }

      // Preserve what the customer already picked when moving between tiers.
      const prev = this.slots || [];
      this.slots = Array.from({ length: qty }, (_, i) => prev[i] || { colour: null, size: null });

      const open = this.slots.findIndex((s) => !this.slotDone(s));
      this.active = open < 0 ? this.slots.length - 1 : open;

      if (this.pickerTitleEl) {
        this.pickerTitleEl.textContent = (el && el.dataset.picker) || this.pickerTitleEl.dataset.default || '';
      }

      this.buildSlotRow();
      this.paintGifts();
      this.paintPicker();
      this.update();
    }

    /* Done means every option this product actually has is chosen. The socks
       and the leg sleeves are sized only, and demanding a colour of them left
       the add button permanently disabled. */
    slotDone(s) {
      if (!s) return false;
      if (this.data.hasColour && !s.colour) return false;
      if (this.data.hasSize && !s.size) return false;
      return true;
    }

    buildSlotRow() {
      const multi = this.qty > 1;
      this.pickerHead.hidden = !multi;
      this.slotRow.hidden = !multi;
      this.slotRow.textContent = '';
      this.chipEls = [];
      if (!multi) return;

      this.slotRow.setAttribute('role', 'radiogroup');
      this.slotRow.setAttribute('aria-label', 'Your items');
      this.slotRow.style.setProperty('--pb-slot-count', String(this.qty));

      this.slots.forEach((_, i) => {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'pb__slotchip';
        chip.setAttribute('role', 'radio');
        chip.setAttribute('aria-checked', 'false');

        const dot = document.createElement('span');
        dot.className = 'pb__slotchip-dot';
        dot.setAttribute('aria-hidden', 'true');
        dot.textContent = String(i + 1);

        const txt = document.createElement('span');
        txt.className = 'pb__slotchip-txt';

        chip.append(dot, txt);
        chip.addEventListener('click', () => this.setActive(i));
        this.slotRow.appendChild(chip);
        this.chipEls.push(chip);
      });
    }

    // The swatch and size rows are identical for every tier and every slot, so
    // they are built exactly once and repainted, never rebuilt.
    buildOptionRows() {
      this.swatchEls = this.data.colours.map((c) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'pb__swatch';
        b.setAttribute('role', 'radio');
        b.setAttribute('aria-checked', 'false');
        b.setAttribute('aria-label', c.name);
        b.title = c.name;
        b.dataset.pbColour = c.name;
        const sw = swatchVars(c.name);
        b.style.setProperty('--sw', sw.base);
        b.style.setProperty('--sw2', sw.accent);
        if (c.image) b.appendChild(swatchImage(c.image, c.name));
        b.addEventListener('click', () => this.pickColour(c.name));
        this.swatchHost.appendChild(b);
        return b;
      });

      this.sizeEls = this.data.sizes.map((s) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'pb__size';
        b.setAttribute('role', 'radio');
        b.setAttribute('aria-checked', 'false');
        b.textContent = s;
        b.dataset.pbSize = s;
        b.addEventListener('click', () => this.pickSize(s));
        this.sizeHost.appendChild(b);
        return b;
      });
    }

    setActive(i) {
      this.active = i;
      this.paintPicker();
    }

    pickColour(colour) {
      const slot = this.slots[this.active];
      slot.colour = colour;
      // Moving the gallery is the point of the interaction, so it happens first.
      this.showColour(colour);
      // A size that does not exist in the new colour cannot stay silently
      // selected, or add to cart would fail on a pick the customer never saw.
      if (slot.size) {
        const v = this.findVariant(colour, slot.size);
        if (!v || !v.available) slot.size = null;
      }
      this.maybeAdvance();
      this.paintPicker();
      this.update();
    }

    pickSize(size) {
      const slot = this.slots[this.active];
      slot.size = size;
      this.lastSize = size;
      this.maybeAdvance();
      this.paintPicker();
      this.update();
    }

    // Once the active slot is complete the controls move on to the next item
    // that still needs a choice, carrying the size forward: one person wears
    // one size, so the usual journey is one size tap and then a colour tap per
    // item. Any slot the size back-fill completes is skipped over the same way.
    maybeAdvance() {
      if (!this.slotDone(this.slots[this.active])) return;
      for (let guard = 0; guard <= this.slots.length; guard++) {
        const i = this.slots.findIndex((s) => !this.slotDone(s));
        if (i < 0) return;
        const s = this.slots[i];
        if (!s.size && this.lastSize && s.colour) {
          const v = this.findVariant(s.colour, this.lastSize);
          if (v && v.available) { s.size = this.lastSize; continue; }
        }
        if (!s.size && this.lastSize && !s.colour) s.size = this.lastSize;
        this.active = i;
        return;
      }
    }

    paintPicker() {
      const slot = this.slots[this.active] || { colour: null, size: null };

      this.chipEls.forEach((chip, i) => {
        const s = this.slots[i];
        const done = this.slotDone(s);
        const activeNow = i === this.active;
        chip.classList.toggle('is-active', activeNow);
        chip.classList.toggle('is-done', done);
        chip.setAttribute('aria-checked', activeNow ? 'true' : 'false');

        const dot = chip.querySelector('.pb__slotchip-dot');
        const txt = chip.querySelector('.pb__slotchip-txt');
        if (s.colour) {
          const sw = swatchVars(s.colour);
          chip.classList.add('has-colour');
          dot.textContent = '';
          dot.style.setProperty('--sw', sw.base);
          dot.style.setProperty('--sw2', sw.accent);
          const picked = this.data.colours.find((c) => c.name === s.colour);
          if (picked && picked.image) dot.appendChild(swatchImage(picked.image, s.colour));
        } else {
          chip.classList.remove('has-colour');
          dot.textContent = String(i + 1);
        }
        txt.textContent = s.size || (s.colour ? 'Size' : 'Choose');
        chip.setAttribute('aria-label',
          `Item ${i + 1} of ${this.qty}: ` +
          (done ? [s.colour, s.size].filter(Boolean).join(', ') : 'not chosen yet'));
      });

      this.swatchEls.forEach((b) => {
        const on = b.dataset.pbColour === slot.colour;
        b.setAttribute('aria-checked', on ? 'true' : 'false');
        b.classList.toggle('is-on', on);
      });

      this.sizeEls.forEach((b) => {
        const on = b.dataset.pbSize === slot.size;
        b.setAttribute('aria-checked', on ? 'true' : 'false');
        b.classList.toggle('is-on', on);
        const v = this.findVariant(slot.colour, b.dataset.pbSize);
        /* Before a colour is picked every size still reads as available, so
           the row does not look sold out before it has been asked a question.
           With no colour to pick, availability is simply the truth. */
        const sellable = (this.data.hasColour && !slot.colour) ? true : Boolean(v && v.available);
        b.classList.toggle('is-out', !sellable);
        b.disabled = !sellable;
      });

      if (this.colourNameEl) this.colourNameEl.textContent = slot.colour || '';

      if (this.progressEl) {
        const chosen = this.slots.filter((s) => this.slotDone(s)).length;
        this.progressEl.textContent = `${chosen} of ${this.qty} picked`;
        this.progressEl.classList.toggle('is-done', chosen === this.qty);
      }
    }

    findVariant(colour, size) {
      /* Only demand the options the product actually has. Demanding both meant
         a product sized only resolved to nothing, so every size read as sold
         out and the add button never enabled. */
      if (this.data.hasColour && !colour) return null;
      if (this.data.hasSize && !size) return null;
      return this.data.variants.find((v) =>
        (!this.data.hasColour || v.colour === colour)
        && (!this.data.hasSize || v.size === size)) || null;
    }

    /* ---------- gallery ---------- */

    showColour(colour) {
      const idx = this.data.mediaByColour[colour];
      if (typeof idx === 'number') this.goTo(idx);
    }

    goTo(i) {
      const slide = this.track.querySelector(`[data-pb-slide="${i}"]`);
      if (!slide) return;
      this.flash(slide);
      // This module runs from inside the section and theme.js runs at the foot
      // of the body, so on the first paint the track is still a plain element
      // with no methods on it. A colour picked that early waits for it.
      if (this.track.go) this.track.go(i);
      else customElements.whenDefined('swipe-track').then(() => this.track.go(i));
    }

    flash(slide) {
      const img = slide.querySelector('.pb__img');
      if (!img) return;
      img.classList.remove('is-in');
      void img.offsetWidth;          // restart the animation
      img.classList.add('is-in');
    }

    syncDots(active) {
      const i = typeof active === 'number' ? active : (this.track.index || 0);
      this.dots.forEach((d, n) => d.classList.toggle('is-on', n === i));
      this.thumbs.forEach((t, n) => t.classList.toggle('is-on', n === i));
      this.activeSlide = i;
      this.track.querySelectorAll('[data-pb-video]').forEach((v) => {
        if (v.closest('[data-pb-slide]').dataset.pbSlide === String(i)) {
          const run = v.play();
          if (run) run.catch(() => {});
        } else if (!v.paused) {
          v.pause();
        }
      });
      if (this.prevBtn) this.prevBtn.disabled = i === 0;
      if (this.nextBtn) this.nextBtn.disabled = i >= this.thumbs.length - 1;
      this.revealThumb(this.thumbs[i]);
      this.markStrip();
    }

    markStrip() {
      const host = this.thumbsHost;
      if (!host) return;
      host.classList.toggle('is-more', host.scrollLeft + host.clientWidth < host.scrollWidth - 1);
    }

    // Keep the marked thumbnail inside its own strip, and move nothing else.
    //
    // This was scrollIntoView, which walks every scrollable ancestor and will
    // move the document to satisfy itself. On a phone the gallery sits above
    // the buy box, so picking a colour threw the page back up to the image and
    // took the size buttons out from under the thumb that was reaching for
    // them. Scrolling the strip by hand is horizontal only and cannot reach
    // the page.
    revealThumb(el) {
      const host = this.thumbsHost;
      if (!el || !host) return;
      const left = el.offsetLeft;
      const right = left + el.offsetWidth;
      const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const behavior = calm ? 'auto' : 'smooth';
      if (left < host.scrollLeft) host.scrollTo({ left, behavior });
      else if (right > host.scrollLeft + host.clientWidth) host.scrollTo({ left: right - host.clientWidth, behavior });
    }

    step(dir) {
      this.goTo((this.activeSlide || 0) + dir);
    }

    /* ---------- gift stack ---------- */

    paintGifts() {
      if (!this.giftEls.length) return;
      let unlocked = 0;
      this.giftEls.forEach((el) => {
        const need = parseInt(el.dataset.minQty, 10) || 1;
        const on = this.qty >= need;
        if (on) unlocked += 1;
        // Only animate the transition into unlocked, never on every repaint.
        if (on && !el.classList.contains('is-on')) {
          el.classList.remove('just-unlocked');
          void el.offsetWidth;
          el.classList.add('just-unlocked');
        }
        el.classList.toggle('is-on', on);
      });
      const all = unlocked === this.giftEls.length;
      if (this.giftTitle) {
        this.giftTitle.textContent = all
          ? (this.dataset.giftsUnlockedTitle || 'Unlocked')
          : this.giftsLockedTitle;
      }
      if (this.giftCount) {
        this.giftCount.textContent = `${unlocked}/${this.giftEls.length}`;
        this.giftCount.classList.toggle('is-on', all);
      }
      const host = this.querySelector('[data-pb-gifts]');
      if (host) host.classList.toggle('is-all', all);
    }

    /* ---------- in-column clip strip ---------- */

    wireStrips() {
      this.wireClipDialog();
      this.wireClipBar('vstrip');
      this.wireClipBar('quotes', 180);
      this.wireRowDrag(this.querySelector('[data-pb-quotes-row]'));
    }

    /**
     * A tap plays the clip inside its own tile, with sound, at the size it is
     * drawn. The <video> is only built on that first tap, so nothing streams
     * before a customer asks. The corner icon hands the clip, at the same
     * moment, to a dialog with the browser's own controls.
     */
    wireClipDialog() {
      const clips = Array.from(this.querySelectorAll('[data-pb-vclip]'));
      const dialog = this.querySelector('[data-pb-vclip-dialog]');
      if (!clips.length) return;

      const video = dialog ? dialog.querySelector('[data-pb-vclip-video]') : null;
      const closeBtn = dialog ? dialog.querySelector('[data-pb-vclip-close]') : null;

      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      let opener = null;
      let muted = false;
      const offscreen = 'IntersectionObserver' in window
        ? new IntersectionObserver((entries) => {
          entries.forEach((e) => {
            if (!e.isIntersecting && !e.target.paused) e.target.pause();
          });
        })
        : null;

      const inlineOf = (tile) => tile.querySelector('.pb__vclip-video');

      const setSound = (tile, vid) => {
        const btn = tile.querySelector('[data-pb-vclip-sound]');
        if (!btn) return;
        btn.setAttribute('aria-pressed', String(vid.muted));
        btn.setAttribute('aria-label', vid.muted ? 'Unmute' : 'Mute');
        tile.classList.toggle('is-muted', vid.muted);
      };

      const build = (tile) => {
        let vid = inlineOf(tile);
        if (vid) return vid;
        vid = document.createElement('video');
        vid.className = 'pb__vclip-video';
        vid.setAttribute('playsinline', '');
        vid.setAttribute('loop', '');
        vid.setAttribute('preload', 'auto');
        vid.tabIndex = -1;
        const still = tile.querySelector('.pb__vclip-still');
        if (still) vid.poster = still.currentSrc || still.src;
        vid.src = tile.dataset.clipSrc;
        vid.muted = muted;
        const toggle = tile.querySelector('[data-pb-vclip-toggle]');
        const sound = tile.querySelector('[data-pb-vclip-sound]');
        vid.addEventListener('play', () => {
          tile.classList.add('is-playing');
          if (toggle) toggle.setAttribute('aria-pressed', 'true');
        });
        vid.addEventListener('pause', () => {
          tile.classList.remove('is-playing');
          if (toggle) toggle.setAttribute('aria-pressed', 'false');
        });
        tile.insertBefore(vid, tile.querySelector('.pb__vclip-veil'));
        if (offscreen) offscreen.observe(vid);
        tile.classList.add('is-live');
        if (sound) sound.hidden = false;
        setSound(tile, vid);
        return vid;
      };

      const pauseOthers = (tile) => {
        clips.forEach((other) => {
          const ov = inlineOf(other);
          if (other !== tile && ov && !ov.paused) ov.pause();
        });
      };

      const toggle = (tile) => {
        if (!tile.dataset.clipSrc) return;
        const vid = build(tile);
        if (vid.paused) {
          pauseOthers(tile);
          vid.play().catch(() => {
            vid.muted = true;
            setSound(tile, vid);
            vid.play().catch(() => {});
          });
        } else {
          vid.pause();
        }
      };

      const open = (tile, btn) => {
        const src = tile.dataset.clipSrc;
        if (!src || !dialog || !video) return;
        opener = btn;

        const inline = inlineOf(tile);
        const at = inline ? inline.currentTime : 0;
        if (inline) inline.pause();

        const still = tile.querySelector('.pb__vclip-still');
        if (still) video.poster = still.currentSrc || still.src;
        video.src = src;
        video.muted = false;
        video.load();
        if (at > 0) {
          video.addEventListener('loadedmetadata', () => { video.currentTime = at; }, { once: true });
        }

        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
        // showModal blocks the page scroll in most browsers and does not on
        // iOS, where a flick still drags the page behind the backdrop.
        document.documentElement.classList.add('pb-noscroll');
        // Read a layout property to flush the display change, so the fade has
        // a start value to run from. A rAF would do the same on screen and
        // never fires in a hidden tab, which would leave the class to land
        // after the dialog had already been closed again.
        void dialog.offsetWidth;
        dialog.classList.add('is-open');
        video.play().catch(() => {});
      };

      clips.forEach((tile) => {
        const hit = tile.querySelector('[data-pb-vclip-toggle]');
        const sound = tile.querySelector('[data-pb-vclip-sound]');
        const expand = tile.querySelector('[data-pb-vclip-expand]');
        if (hit) hit.addEventListener('click', () => toggle(tile));
        if (sound) {
          sound.addEventListener('click', () => {
            const vid = inlineOf(tile);
            if (!vid) return;
            vid.muted = !vid.muted;
            muted = vid.muted;
            setSound(tile, vid);
          });
        }
        if (expand) expand.addEventListener('click', () => open(tile, expand));
      });

      if (!dialog || !video) return;

      const shut = () => {
        if (!dialog.open) return;
        // Silence it now rather than on the close event. The dialog is faded
        // out on a timer and a backgrounded tab holds timers back, which
        // would otherwise leave a clip talking behind a dismissed dialog.
        video.pause();
        dialog.classList.remove('is-open');
        window.setTimeout(() => {
          if (dialog.open) dialog.close();
        }, reduced.matches ? 0 : 220);
      };

      // Escape closes without going through shut(), so the teardown hangs off
      // the event the browser fires either way.
      dialog.addEventListener('close', () => {
        dialog.classList.remove('is-open');
        video.pause();
        const tile = opener ? opener.closest('[data-pb-vclip]') : null;
        const inline = tile ? inlineOf(tile) : null;
        if (inline && video.currentTime > 0) inline.currentTime = video.currentTime;
        video.removeAttribute('src');
        video.removeAttribute('poster');
        video.load();
        document.documentElement.classList.remove('pb-noscroll');
        if (opener) {
          opener.focus();
          opener = null;
        }
      });

      // The surface is centred inside the padded dialog, so anything that
      // reaches the dialog itself was a click on the backdrop.
      dialog.addEventListener('click', (e) => {
        if (e.target === dialog) shut();
      });
      if (closeBtn) closeBtn.addEventListener('click', shut);
    }

    /**
     * The rail's own scrollbar. The thumb is sized from the content rather
     * than fixed, so its width says how much of the row is off screen, and
     * dragging it drives the real scroll position rather than a transform.
     */
    wireClipBar(name, fixed) {
      const row = this.querySelector('[data-pb-' + name + '-row]');
      const bar = this.querySelector('[data-pb-' + name + '-bar]');
      const thumb = this.querySelector('[data-pb-' + name + '-thumb]');
      if (!row || !bar || !thumb) return;

      const MIN_THUMB = 40;
      let span = 0;      // how far the thumb can travel
      let maxScroll = 0; // how far the row can scroll
      let dragging = false;
      let grab = 0;      // where inside the thumb the pointer went down

      const place = () => {
        const ratio = maxScroll > 0 ? row.scrollLeft / maxScroll : 0;
        thumb.style.transform = 'translateX(' + Math.round(ratio * span) + 'px)';
      };

      const measure = () => {
        maxScroll = row.scrollWidth - row.clientWidth;
        // A row that fits gets no track, so nothing on screen offers a drag
        // that would do nothing.
        const overflows = maxScroll > 1;
        bar.hidden = !overflows;
        if (!overflows) return;
        const track = bar.clientWidth;
        const size = fixed
          ? Math.min(fixed, track)
          : Math.max(MIN_THUMB, Math.round(track * (row.clientWidth / row.scrollWidth)));
        thumb.style.width = size + 'px';
        span = Math.max(0, track - size);
        place();
      };

      const scrollToThumb = (left) => {
        if (span <= 0) return;
        const clamped = Math.min(span, Math.max(0, left));
        row.scrollLeft = (clamped / span) * maxScroll;
      };

      const pointAt = (e) => e.clientX - bar.getBoundingClientRect().left - grab;

      bar.addEventListener('pointerdown', (e) => {
        if (bar.hidden) return;
        const box = thumb.getBoundingClientRect();
        const onThumb = e.clientX >= box.left && e.clientX <= box.right;
        grab = onThumb ? e.clientX - box.left : box.width / 2;
        dragging = true;
        // Mandatory snapping fights a drag frame by frame. It comes off for
        // the drag and goes back on release, which is what settles the row on
        // a tile when the pointer is let go.
        row.classList.add('is-dragging');
        bar.setPointerCapture(e.pointerId);
        scrollToThumb(pointAt(e));
        e.preventDefault();
      });

      bar.addEventListener('pointermove', (e) => {
        if (!dragging) return;
        scrollToThumb(pointAt(e));
      });

      const release = (e) => {
        if (!dragging) return;
        dragging = false;
        row.classList.remove('is-dragging');
        if (bar.hasPointerCapture(e.pointerId)) bar.releasePointerCapture(e.pointerId);
      };
      bar.addEventListener('pointerup', release);
      bar.addEventListener('pointercancel', release);

      row.addEventListener('scroll', place, { passive: true });

      if ('ResizeObserver' in window) new ResizeObserver(measure).observe(row);
      else window.addEventListener('resize', measure);
      measure();
    }

    /**
     * Mouse drag on a row, so a desktop visitor without a trackpad can pull
     * the cards across. Touch already scrolls natively. On release the row
     * glides to the nearest card and only then gets its snapping back.
     */
    wireRowDrag(row) {
      if (!row) return;
      let down = false;
      let startX = 0;
      let startLeft = 0;

      const settle = () => {
        const cards = Array.from(row.children);
        const pad = parseFloat(getComputedStyle(row).scrollPaddingInlineStart) || 0;
        const max = row.scrollWidth - row.clientWidth;
        let target = row.scrollLeft;
        let best = Infinity;
        cards.forEach((card) => {
          const left = Math.min(max, Math.max(0, card.offsetLeft - row.offsetLeft - pad));
          const d = Math.abs(left - row.scrollLeft);
          if (d < best) { best = d; target = left; }
        });
        row.scrollTo({ left: target, behavior: 'smooth' });
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          row.classList.remove('is-dragging');
        };
        row.addEventListener('scrollend', finish, { once: true });
        setTimeout(finish, 500);
      };

      row.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        down = true;
        startX = e.clientX;
        startLeft = row.scrollLeft;
        row.classList.add('is-dragging');
        row.setPointerCapture(e.pointerId);
        e.preventDefault();
      });
      row.addEventListener('pointermove', (e) => {
        if (!down) return;
        row.scrollLeft = startLeft - (e.clientX - startX);
      });
      const up = (e) => {
        if (!down) return;
        down = false;
        if (row.hasPointerCapture(e.pointerId)) row.releasePointerCapture(e.pointerId);
        settle();
      };
      row.addEventListener('pointerup', up);
      row.addEventListener('pointercancel', up);
    }

    /* ---------- size guide ---------- */

    wireSizeGuide() {
      const open = this.querySelector('[data-pb-sizeguide]');
      const close = this.querySelector('[data-pb-sizeguide-close]');
      if (!this.sgDialog || !open) return;

      open.addEventListener('click', () => {
        // showModal gives focus trapping, Escape and an inert background free.
        if (typeof this.sgDialog.showModal === 'function') this.sgDialog.showModal();
        else this.sgDialog.setAttribute('open', '');
      });
      if (close) close.addEventListener('click', () => this.sgDialog.close());

      // Clicking the backdrop closes it. The dialog's own box is excluded by
      // testing the click point against its rect.
      this.sgDialog.addEventListener('click', (e) => {
        const r = this.sgDialog.getBoundingClientRect();
        const inside =
          e.clientX >= r.left && e.clientX <= r.right &&
          e.clientY >= r.top && e.clientY <= r.bottom;
        if (!inside) this.sgDialog.close();
      });

      this.sgDialog.querySelectorAll('[data-pb-unit]').forEach((btn) => {
        btn.addEventListener('click', () => this.setUnit(btn.dataset.pbUnit));
      });
    }

    setUnit(unit) {
      this.sgDialog.querySelectorAll('[data-pb-unit]').forEach((b) => {
        const on = b.dataset.pbUnit === unit;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      this.sgDialog.querySelectorAll('[data-sg-kg]').forEach((e) => { e.hidden = unit !== 'kg'; });
      this.sgDialog.querySelectorAll('[data-sg-lb]').forEach((e) => { e.hidden = unit !== 'lb'; });
    }

    /* ---------- totals and cart ---------- */

    update() {
      const ready = this.slots.every((s) => {
        const v = this.findVariant(s.colour, s.size);
        return v && v.available;
      });
      this.atc.disabled = !ready;
      this.atc.classList.toggle('is-ready', ready);

      // With the standalone price row gone, the button carries the figure.
      if (this.atcLabelEl) {
        this.atcLabelEl.textContent = `${this.atcLabel} · ${this.fmt(this.totalFor(this.qty))}`;
      }

      if (!ready) {
        const n = this.slots.filter((s) => !this.slotDone(s)).length;
        /* Name only what this product actually asks for. The socks and the leg
           sleeves have no colour, and telling a customer to pick one sends
           them looking for a row that is not on the page. */
        const what = this.data.hasColour
          ? (this.data.hasSize ? 'a colour and size' : 'a colour')
          : 'a size';
        this.note.textContent = n === this.qty
          ? `Pick ${what} to continue`
          : `${n} more to choose`;
      } else {
        this.note.textContent = '';
      }

    }

    async addToCart() {
      const variants = this.slots
        .map((s) => this.findVariant(s.colour, s.size))
        .filter(Boolean);
      if (variants.length !== this.qty) return;

      /* Shopify's checkout strikes the line's real price and never carries
         compare-at, so the only way the full price reaches checkout, the order
         and the confirmation email is as a line item property. Named without a
         leading underscore so it is shown rather than hidden, and only added
         when there is a genuine higher figure to quote. */
      const RRP = 'Full price';
      const rrpOf = (v) => (v && v.compareAt > v.price ? { [RRP]: this.fmt(v.compareAt) } : {});

      let items;
      const drawerEl = document.querySelector('[data-cart-drawer]');
      const asLine = !!drawerEl && drawerEl.dataset.freeLine === 'true';
      if (asLine) {
        // Every garment is a real line, the free ones included: the automatic
        // discount prices those at zero, so the order, the packing slip and the
        // confirmation email all show what the customer is actually getting.
        // A marker per garment keeps two of the same colour and size as two
        // rows, each with its own pencil, and _free marks the ones the offer
        // gave so removing a chosen one takes its bonus with it.
        const paid = this.paidUnits(this.qty);
        const stamp = Date.now();
        items = variants.map((v, i) => ({
          id: v.id,
          quantity: 1,
          properties: Object.assign({}, rrpOf(v), i < paid
            ? { _i: `${stamp}-${i}` }
            // Chosen here, on the product page, so the cart has nothing to ask.
            : { _i: `${stamp}-${i}`, _free: '1', _picked: '1' }),
        }));
      } else {
        // Only the paid garments are real lines, because a theme cannot make a
        // line cost nothing and two at 20 is exactly the 40 the tier promises.
        // The free one rides on the first line as a property, which is what
        // the order and the packing slip show, and what the cart renders as
        // its own FREE row.
        const paid = this.paidUnits(this.qty);
        /* The free garment travels as the variant's own option values, joined
           in the product's order, which is what the cart reads it back with. */
        const free = this.slots.slice(paid).map((s) => {
          const v = this.findVariant(s.colour, s.size);
          return v ? v.spec : [s.colour, s.size].filter(Boolean).join(', ');
        });
        // A marker per garment so two of the same colour and size stay two
        // rows in the cart, each with its own pencil, rather than collapsing
        // into one row of quantity two that can only be re-specced as a pair.
        const stamp = Date.now();
        items = variants.slice(0, paid).map((v, i) => ({
          id: v.id, quantity: 1, properties: Object.assign({}, rrpOf(v), { _i: `${stamp}-${i}` }),
        }));
        /* Only when the drawer is present and says so. With no drawer to
           read, the cart's own top-up awards the free garment on the next
           render, in whichever mode the store is running, so nothing is
           written here that the other mode would draw as a second free row. */
        if (free.length && items.length && drawerEl && drawerEl.dataset.freeLine === 'false') {
          const drawer = drawerEl;
          const label = (drawer && drawer.dataset.freeProp) || 'Third one, free';
          items[0].properties[label] = free.join(' + ');
          // Chosen here, on the product page, so the cart has nothing to ask.
          // Only a free garment the cart added by itself arrives unchosen, and
          // that is the one whose picker opens and asks.
          items[0].properties._picked = '1';
        }
      }


      this.atc.classList.add('is-busy');
      this.atc.disabled = true;

      // The drawer re-renders from section HTML returned by /cart/add.js. Asking
      // for the section in the same request that adds the lines means the cart
      // is never painted from a second, racing fetch, which is how a drawer ends
      // up showing the previous state.
      try {
        const drawer = document.querySelector('[data-cart-drawer]');
        const drawerSection = drawer ? drawer.dataset.sectionId : null;

        // Gift products ride along as real zero-price lines, marked _gift so
        // the drawer knows they are furniture. They only apply to a
        // multi-garment tier, which is any tier that earns a free one.
        const giftIds = (this.dataset.autoGifts || '')
          .split(',').map((v) => Number(v)).filter(Boolean);
        const earnsFree = this.qty > this.paidUnits(this.qty);
        const payload = earnsFree && giftIds.length
          ? items.concat(giftIds.map((id) => ({ id, quantity: 1, properties: { _gift: '1' } })))
          : items;

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: payload, sections: drawerSection || undefined }),
        });
        const body = await res.json();
        if (body.status) throw new Error(body.message || 'Add to cart failed');

        // This response already carries the rendered drawer, so the drawer can
        // be painted and opened from it with nothing further fetched. The
        // count and the gift sweep need the whole cart, which /cart/add.js
        // does not return, but neither is worth making the customer wait for:
        // the drawer decides when to go and get them.
        const sectionHtml = body.sections && drawerSection ? body.sections[drawerSection] : null;

        // The buy box does not know how the drawer is built. It announces the
        // change and the drawer decides what to do with it.
        document.dispatchEvent(
          new CustomEvent('cart:changed', { detail: { sectionHtml, source: 'product-buy' } })
        );

        this.note.textContent = 'Added to your cart';
      } catch (e) {
        this.note.textContent = 'Could not add that. Please try again.';
      } finally {
        this.atc.classList.remove('is-busy');
        this.atc.disabled = false;
      }
    }
  }

  customElements.define('product-buy', ProductBuy);
})();
